import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tooltip,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MarketplaceCardResponseDto } from "../../types/types";
import { buildMarketCardView } from "./marketCardHelpers";
import calendarIcon from "../../assets/marketplaceCards/calendarIcon.svg";
import deleteIcon from "../../assets/marketplaceCards/deleteIcon.svg";
import editIcon from "../../assets/marketplaceCards/editIcon.svg";
import gloveIcon from "../../assets/marketplaceCards/gloveIcon.svg";
import graduationCapIcon from "../../assets/marketplaceCards/graduationCapIcon.svg";
import mapPinIcon from "../../assets/marketplaceCards/mapPinIcon.svg";
import trophyIcon from "../../assets/marketplaceCards/trophyIcon.svg";
import weightIcon from "../../assets/marketplaceCards/weightIcon.svg";

export interface MarketplaceCardEditPayload {
  type: "READYROSTER" | "OPPORTUNITY";
  data: MarketplaceCardResponseDto;
}

interface MarketplaceCardProps {
  item: MarketplaceCardResponseDto;
  role: string;
  activeTab: "all" | "myCards";
  onView: () => void;
  onEdit: (payload: MarketplaceCardEditPayload) => void;
  onDelete: () => void;
}

interface InfoItemProps {
  icon: React.ReactNode;
  text: string;
  tooltip?: string;
}

// Renders an empty cell when there is no text so the grid columns never shift.
const InfoItem: React.FC<InfoItemProps> = ({ icon, text, tooltip }) =>
  text ? (
    <Tooltip content={tooltip ? `${tooltip}: ${text}` : text}>
      <div className="flex items-center gap-1 min-w-0">
        {icon}
        <span className="truncate">{text}</span>
      </div>
    </Tooltip>
  ) : (
    <div />
  );

const ROW_CLASS = "grid grid-cols-[118px_minmax(0,1fr)] gap-x-4";
const ICON_CLASS = "w-4 h-4 flex-shrink-0";

const CardIcon: React.FC<{ src: string }> = ({ src }) => (
  <img src={src} alt="" aria-hidden="true" className={ICON_CLASS} />
);

const STATUS_BADGE_CLASS =
  "text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap";

const MarketplaceCard: React.FC<MarketplaceCardProps> = ({
  item,
  role,
  activeTab,
  onView,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation("marketPlaceCards");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);

  const view = buildMarketCardView(item);
  if (!view) return null;

  const isMyCards = activeTab === "myCards";
  const { fighter, manager } = {
    fighter: item.readyRosterCard?.fighter,
    manager: item.opportunityCardResponse?.manager,
  };

  const canEditOrDelete =
    isMyCards &&
    (view.isRoster ? !view.isPublished : view.isAvailable) &&
    ((role === "FIGHTER" && fighter) ||
      ((role === "MANAGER" || role === "ORGANIZATION") && manager));

  let backgroundClass = "bg-white";
  if (view.isExpired) backgroundClass = "bg-red-50";
  else if (isMyCards && view.isPublished) backgroundClass = "bg-gray-50";

  const status = view.status?.toUpperCase();

  return (
    <>
      <div
        onClick={onView}
        className={`${backgroundClass} relative flex flex-col w-full h-96 rounded-3xl cursor-pointer overflow-hidden box-border font-['Plus_Jakarta_Sans'] text-zinc-900 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.06),0px_2px_4px_0px_rgba(0,0,0,0.04),inset_0px_0px_1px_0px_rgba(0,0,0,0.30)] hover:shadow-md transition-shadow`}
      >
        <div className="p-1 flex flex-col gap-2 flex-shrink-0">
          <div className="relative h-[195px] rounded-[20px] overflow-hidden bg-gray-100">
            {view.imageSrc ? (
              <img
                src={view.imageSrc}
                alt={view.title}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div
                className={`w-full h-full flex items-center justify-center text-3xl font-bold ${
                  view.isRoster
                    ? "bg-blue-100 text-blue-600"
                    : "bg-purple-100 text-purple-600"
                }`}
              >
                {view.initials}
              </div>
            )}

            {isMyCards && (
              <div className="absolute top-2 left-2 flex gap-1 flex-wrap max-w-[60%]">
                {status === "PUBLISH" && (
                  <span className={`${STATUS_BADGE_CLASS} text-green-700 bg-green-100`}>
                    Published
                  </span>
                )}
                {status === "AVAILABLE" && (
                  <span className={`${STATUS_BADGE_CLASS} text-blue-700 bg-blue-100`}>
                    Available
                  </span>
                )}
                {view.isExpired && (
                  <span className={`${STATUS_BADGE_CLASS} text-red-700 bg-red-100`}>
                    Expired
                  </span>
                )}
              </div>
            )}

            <div
              className="absolute top-2 right-2 flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {view.hasEvent && (
                <Tooltip
                  content={
                    item.opportunityCardResponse?.eventName || "Event Opportunity"
                  }
                >
                  <div className="h-7 w-7 flex items-center justify-center rounded-full bg-white/90">
                    <CardIcon src={trophyIcon} />
                  </div>
                </Tooltip>
              )}

              {canEditOrDelete && (
                <>
                  <Tooltip content={t("edit")}>
                    <Button
                      isIconOnly
                      size="sm"
                      variant="flat"
                      onPress={() =>
                        onEdit({
                          type: view.isRoster ? "READYROSTER" : "OPPORTUNITY",
                          data: item,
                        })
                      }
                      className="h-7 w-7 min-w-7 bg-white/90 text-blue-600 hover:bg-blue-50 rounded-full"
                    >
                      <CardIcon src={editIcon} />
                    </Button>
                  </Tooltip>
                  <Tooltip content={t("delete")} color="danger">
                    <Button
                      isIconOnly
                      size="sm"
                      variant="flat"
                      onPress={() => setIsDeleteConfirmOpen(true)}
                      className="h-7 w-7 min-w-7 bg-white/90 text-red-600 hover:bg-red-50 rounded-full"
                    >
                      <CardIcon src={deleteIcon} />
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>

          {view.pill && (
            <div
              className={`${view.pill.className} h-5 w-full rounded-full flex items-center justify-center px-2 flex-shrink-0`}
            >
              <span className="text-xs font-bold leading-4 truncate">
                {view.pill.label}
              </span>
            </div>
          )}
        </div>

        {/* Rows are direct children so justify-between spreads them exactly like Figma
            (fighter card: 4 children, opportunity card: 3 children). */}
        <div className="flex-1 min-h-0 px-4 pt-1 pb-4 flex flex-col justify-between text-sm leading-5">
          <div className="h-12 flex justify-between items-start gap-2">
            <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
              <div
                className={`text-base font-bold leading-6 ${
                  view.subtitle ? "line-clamp-1" : "line-clamp-2"
                }`}
                title={view.title}
              >
                {view.title}
              </div>
              {view.subtitle && (
                <div className="text-xs leading-4 text-zinc-500 truncate">
                  {view.subtitle}
                </div>
              )}
            </div>
            {view.purse && (
              <Tooltip content={t("purse")}>
                <div className="text-sm font-bold leading-5 whitespace-nowrap flex-shrink-0">
                  {view.purse}
                </div>
              </Tooltip>
            )}
          </div>

          {view.isRoster ? (
            <>
              <div className={ROW_CLASS}>
                <InfoItem
                  icon={<CardIcon src={gloveIcon} />}
                  text={view.sport}
                  tooltip={t("sport")}
                />
                <InfoItem
                  icon={<CardIcon src={gloveIcon} />}
                  text={view.level}
                  tooltip={t("preferred_ruleset")}
                />
              </div>

              <div className={ROW_CLASS}>
                <InfoItem
                  icon={<CardIcon src={weightIcon} />}
                  text={view.weightClass}
                  tooltip={t("weight_class")}
                />
                <InfoItem
                  icon={<CardIcon src={mapPinIcon} />}
                  text={view.location}
                  tooltip={t("location")}
                />
              </div>
            </>
          ) : (
            <>
              {view.sport && (
                <div className={ROW_CLASS}>
                  <InfoItem
                    icon={<CardIcon src={gloveIcon} />}
                    text={view.sport}
                    tooltip={t("sport")}
                  />
                </div>
              )}

              <div className={ROW_CLASS}>
                <InfoItem
                  icon={<CardIcon src={calendarIcon} />}
                  text={view.date}
                />
                <InfoItem
                  icon={<CardIcon src={graduationCapIcon} />}
                  text={view.level}
                  tooltip={t("preferred_ruleset")}
                />
              </div>

              <div className={ROW_CLASS}>
                <InfoItem
                  icon={<CardIcon src={weightIcon} />}
                  text={view.weightClass}
                  tooltip={t("weight_class")}
                />
                <InfoItem
                  icon={<CardIcon src={mapPinIcon} />}
                  text={view.location}
                  tooltip={t("location")}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        size="sm"
        backdrop="transparent"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-lg font-semibold text-gray-800">
                {t("confirm_delete_title")}
              </ModalHeader>
              <ModalBody className="text-gray-600">
                {t("confirm_delete_message")}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="light"
                  onPress={onClose}
                  className="text-gray-600"
                >
                  {t("cancel")}
                </Button>
                <Button
                  color="danger"
                  onPress={() => {
                    onDelete();
                    onClose();
                  }}
                >
                  {t("delete")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default MarketplaceCard;
